import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronDown, ChevronUp, Trash2, Wand2, Droplets, Sun, Waves, Clock, Bell, Play, Check, X, AlertCircle, Sparkles, CalendarDays } from 'lucide-react';
import PageShell from '../components/PageShell';
import { motion, AnimatePresence } from 'framer-motion';
import ProGate, { ProBadge } from '../components/ProGate';
import { useSubscription } from '../contexts/SubscriptionContext';
import { authFetch, API } from '../utils/api';


const TYPE_ICONS = {
  hydration: Droplets,
  sunscreen: Sun,
  conditions: Waves,
  briefing: Clock,
  reminder: Bell,
  tide: Waves,
  smart_sessions: Sparkles,
  custom: Wand2,
};

const TYPE_COLORS = {
  hydration: 'text-blue-400',
  sunscreen: 'text-yellow-400',
  conditions: 'text-primary',
  briefing: 'text-orange-400',
  reminder: 'text-green-400',
  tide: 'text-teal-400',
  smart_sessions: 'text-amber-400',
  custom: 'text-purple-400',
};

function ConfigEditor({ auto, onSave }) {
  const [config, setConfig] = useState(auto.config || {});
  const [dirty, setDirty] = useState(false);

  const update = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const handleSave = () => {
    onSave(config);
    setDirty(false);
  };

  const type = auto.type;

  return (
    <div className="space-y-2.5 pt-2" data-testid={`config-editor-${auto.automation_id}`}>
      {type === 'hydration' && (
        <div>
          <label className="text-[10px] text-muted-foreground block mb-1">Remind every (minutes)</label>
          <input
            data-testid="config-interval"
            type="number"
            value={config.interval_minutes || 30}
            onChange={e => update('interval_minutes', parseInt(e.target.value) || 30)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-primary/50"
          />
        </div>
      )}
      {type === 'sunscreen' && (
        <>
          <div>
            <label className="text-[10px] text-muted-foreground block mb-1">UV threshold</label>
            <input
              data-testid="config-uv-threshold"
              type="number"
              value={config.uv_threshold || 6}
              onChange={e => update('uv_threshold', parseInt(e.target.value) || 6)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-primary/50"
            />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground block mb-1">Reapply every (minutes)</label>
            <input
              data-testid="config-reapply"
              type="number"
              value={config.reapply_minutes || 90}
              onChange={e => update('reapply_minutes', parseInt(e.target.value) || 90)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-primary/50"
            />
          </div>
        </>
      )}
      {type === 'conditions' && (
        <>
          <div>
            <label className="text-[10px] text-muted-foreground block mb-1">Spot</label>
            <input
              data-testid="config-spot"
              type="text"
              value={config.spot || ''}
              onChange={e => update('spot', e.target.value)}
              placeholder="e.g. Huntington Beach"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-primary/50 placeholder:text-muted-foreground/40"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-muted-foreground block mb-1">Min wave (ft)</label>
              <input
                data-testid="config-min-wave"
                type="number"
                value={config.min_wave_ft || 2}
                onChange={e => update('min_wave_ft', parseInt(e.target.value) || 2)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-primary/50"
              />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground block mb-1">Max wind (kph)</label>
              <input
                data-testid="config-max-wind"
                type="number"
                value={config.max_wind_kph || 25}
                onChange={e => update('max_wind_kph', parseInt(e.target.value) || 25)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-primary/50"
              />
            </div>
          </div>
        </>
      )}
      {type === 'briefing' && (
        <>
          <div>
            <label className="text-[10px] text-muted-foreground block mb-1">Briefing time</label>
            <input
              data-testid="config-brief-time"
              type="time"
              value={config.time || '06:00'}
              onChange={e => update('time', e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-primary/50"
            />
          </div>
          <div>
            <label className="text-[10px] text-muted-foreground block mb-1">Spot</label>
            <input
              data-testid="config-brief-spot"
              type="text"
              value={config.spot || ''}
              onChange={e => update('spot', e.target.value)}
              placeholder="your home break"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-primary/50 placeholder:text-muted-foreground/40"
            />
          </div>
        </>
      )}
      {type === 'reminder' && (
        <div>
          <label className="text-[10px] text-muted-foreground block mb-1">Reminder time</label>
          <input
            data-testid="config-reminder-time"
            type="time"
            value={config.time || '18:00'}
            onChange={e => update('time', e.target.value)}
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-primary/50"
          />
        </div>
      )}
      {type === 'tide' && (
        <>
          <div>
            <label className="text-[10px] text-muted-foreground block mb-1">Spot</label>
            <input
              data-testid="config-tide-spot"
              type="text"
              value={config.spot || ''}
              onChange={e => update('spot', e.target.value)}
              placeholder="e.g. Trestles"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-primary/50 placeholder:text-muted-foreground/40"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-muted-foreground block mb-1">Preferred tide</label>
              <select
                data-testid="config-tide-pref"
                value={config.preferred_tide || 'Low'}
                onChange={e => update('preferred_tide', e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-primary/50"
              >
                <option value="Low">Low</option>
                <option value="Mid">Mid</option>
                <option value="High">High</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground block mb-1">Lead time (min)</label>
              <input
                data-testid="config-tide-lead"
                type="number"
                value={config.lead_minutes || 60}
                onChange={e => update('lead_minutes', parseInt(e.target.value) || 60)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-primary/50"
              />
            </div>
          </div>
        </>
      )}
      {type === 'smart_sessions' && (
        <>
          <div>
            <label className="text-[10px] text-muted-foreground block mb-1">Primary spot (leave blank for home break)</label>
            <input
              data-testid="config-smart-spot"
              type="text"
              value={config.spot || ''}
              onChange={e => update('spot', e.target.value)}
              placeholder="uses home break if empty"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-primary/50 placeholder:text-muted-foreground/40"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] text-muted-foreground block mb-1">Min wave (ft)</label>
              <input
                data-testid="config-smart-min-wave"
                type="number"
                value={config.min_wave_ft || 2}
                onChange={e => update('min_wave_ft', parseInt(e.target.value) || 2)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-primary/50"
              />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground block mb-1">Days ahead</label>
              <input
                data-testid="config-smart-days"
                type="number"
                value={config.days_ahead || 7}
                onChange={e => update('days_ahead', Math.min(14, Math.max(1, parseInt(e.target.value) || 7)))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-primary/50"
              />
            </div>
          </div>
          <div className="bg-amber-500/5 border border-amber-500/10 rounded-lg p-2 mt-1">
            <p className="text-[9px] text-amber-400/70 leading-relaxed">Swell analyzes your preferences, surf history & forecast to suggest optimal sessions — shown on your Calendar page.</p>
          </div>
        </>
      )}
      {dirty && (
        <button
          data-testid="config-save-btn"
          onClick={handleSave}
          className="w-full bg-primary/20 text-primary text-xs font-bold py-1.5 rounded-lg hover:bg-primary/30 transition-colors inline-flex items-center justify-center gap-1"
        >
          <Check className="w-3 h-3" /> Save Config
        </button>
      )}
    </div>
  );
}

function AlertItem({ alert }) {
  const Icon = TYPE_ICONS[alert.type] || Wand2;
  const timeAgo = getTimeAgo(alert.created_at);

  return (
    <div className={`flex items-start gap-3 p-3 rounded-xl transition-colors ${alert.read ? 'bg-white/2 opacity-60' : 'bg-primary/5 border border-primary/10'}`}
      data-testid={`alert-${alert.alert_id}`}
    >
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${TYPE_COLORS[alert.type] || 'text-primary'}`} />
      <div className="flex-1 min-w-0">
        <p className="text-xs leading-relaxed">{alert.message}</p>
        <p className="text-[9px] text-muted-foreground mt-1">{alert.automation_name} &middot; {timeAgo}</p>
      </div>
      {!alert.read && <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-1.5" />}
    </div>
  );
}

function getTimeAgo(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function Automations() {
  const navigate = useNavigate();
  const { isPro } = useSubscription();
  const [automations, setAutomations] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showGallery, setShowGallery] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [triggering, setTriggering] = useState(null);
  const [showProModal, setShowProModal] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [autoRes, galleryRes, alertsRes] = await Promise.all([
        authFetch(`${API}/api/automations`),
        authFetch(`${API}/api/automations/gallery`),
        authFetch(`${API}/api/automations/alerts`),
      ]);
      if (autoRes.ok) setAutomations((await autoRes.json()).automations || []);
      if (galleryRes.ok) setGallery((await galleryRes.json()).featured || []);
      if (alertsRes.ok) {
        const alertData = await alertsRes.json();
        setAlerts(alertData.alerts || []);
        setUnreadCount(alertData.unread_count || 0);
      }
    } catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const addFromGallery = async (item) => {
    try {
      const res = await authFetch(`${API}/api/automations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
          name: item.name,
          type: item.type,
          description: item.description,
          enabled: true,
          config: item.default_config || {},
        }),
      });
      if (res.ok) {
        loadData();
        setShowGallery(false);
      }
    } catch {}
  };

  const toggleEnabled = async (auto) => {
    try {
      await authFetch(`${API}/api/automations/${auto.automation_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ enabled: !auto.enabled }),
      });
      loadData();
    } catch {}
  };

  const updateConfig = async (auto, newConfig) => {
    try {
      await authFetch(`${API}/api/automations/${auto.automation_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ config: newConfig }),
      });
      loadData();
    } catch {}
  };

  const deleteAutomation = async (autoId) => {
    try {
      await authFetch(`${API}/api/automations/${autoId}`, { method: 'DELETE',  });
      loadData();
    } catch {}
  };

  const triggerAutomation = async (autoId) => {
    setTriggering(autoId);
    try {
      await authFetch(`${API}/api/automations/${autoId}/trigger`, {
        method: 'POST',
              });
      await loadData();
    } catch {} finally {
      setTriggering(null);
    }
  };

  const clearAlerts = async () => {
    await authFetch(`${API}/api/automations/alerts`, { method: 'DELETE',  });
    setAlerts([]);
    setUnreadCount(0);
  };

  const filters = ['all', 'hydration', 'conditions', 'reminders'];
  const filtered = filter === 'all' ? automations : automations.filter(a => a.type === filter || (filter === 'reminders' && ['reminder', 'briefing'].includes(a.type)));

  if (loading) {
    return (
      <PageShell title="Swell Skills">
        <div className="flex items-center justify-center py-20">
          <div className="flex space-x-2">
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
            <div className="w-2 h-2 bg-primary rounded-full typing-dot"></div>
          </div>
        </div>
      </PageShell>
    );
  }

  // Alerts view
  if (showAlerts) {
    return (
      <PageShell title="Alerts">
        <div className="p-5 space-y-3" data-testid="alerts-page">
          <div className="flex items-center justify-between mb-2">
            <button
              data-testid="alerts-back-btn"
              onClick={() => setShowAlerts(false)}
              className="text-xs text-primary font-bold hover:opacity-80"
            >
              Back
            </button>
            {alerts.length > 0 && (
              <button
                data-testid="alerts-clear-btn"
                onClick={clearAlerts}
                className="text-[10px] text-muted-foreground hover:text-accent transition-colors"
              >
                Clear all
              </button>
            )}
          </div>
          {alerts.length > 0 ? (
            <div className="space-y-2">
              {alerts.map(a => <AlertItem key={a.alert_id} alert={a} />)}
            </div>
          ) : (
            <div className="text-center py-12">
              <Bell className="w-8 h-8 text-muted-foreground/50 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">no alerts yet</p>
              <p className="text-[10px] text-muted-foreground/60 mt-1">trigger an automation to see alerts here</p>
            </div>
          )}
        </div>
      </PageShell>
    );
  }

  const gatedAction = (fn) => {
    if (!isPro) { setShowProModal(true); return; }
    fn();
  };

  if (showGallery || automations.length === 0) {
    return (
      <PageShell title="Skill Gallery">
        <div className="p-5 space-y-4">
          {/* Pro upgrade banner for free users */}
          {!isPro && (
            <div className="bg-primary/5 border border-primary/15 rounded-2xl p-3 flex items-center gap-3">
              <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
              <p className="text-[11px] text-muted-foreground flex-1">Upgrade to <span className="text-primary font-semibold">Swell Pro</span> to activate skills</p>
              <ProBadge />
            </div>
          )}

          {/* Pro Gate Modal */}
          <AnimatePresence>
            {showProModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                onClick={() => setShowProModal(false)}
              >
                <motion.div
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.95, opacity: 0 }}
                  onClick={e => e.stopPropagation()}
                  className="w-full max-w-sm"
                >
                  <ProGate feature="automations" />
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="bg-bg-subtle/80 border border-white/10 rounded-2xl p-5 flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Custom Skills</p>
              <p className="text-xs text-muted-foreground mt-0.5">Ask Swell to create or edit</p>
            </div>
            <button
              data-testid="automation-custom-btn"
              onClick={() => navigate('/chat')}
              className="bg-primary text-primary-foreground text-xs font-bold px-4 py-2 rounded-xl"
            >
              Message
            </button>
          </div>

          {automations.length > 0 && (
            <button
              data-testid="gallery-back-btn"
              onClick={() => setShowGallery(false)}
              className="text-xs text-primary font-medium hover:underline"
            >
              Back to active skills
            </button>
          )}

          <div>
            <p className="font-heading text-sm font-semibold mb-1">Featured</p>
            <p className="text-xs text-muted-foreground mb-3">Skills for surfers</p>
            <div className="grid grid-cols-2 gap-3">
              {gallery.map((item) => {
                const Icon = TYPE_ICONS[item.type] || Wand2;
                const isActive = automations.some(a => a.type === item.type);
                return (
                  <button
                    key={item.id}
                    data-testid={`gallery-item-${item.id}`}
                    onClick={() => !isActive && gatedAction(() => addFromGallery(item))}
                    className={`bg-bg-subtle/80 border rounded-2xl p-4 text-left transition-colors ${isActive ? 'border-primary/30 opacity-70' : 'border-white/5 hover:border-primary/30'}`}
                    disabled={isActive}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <Icon className={`w-4 h-4 ${TYPE_COLORS[item.type] || 'text-primary'}`} />
                      {isActive && <span className="w-4 h-4 bg-primary rounded-full flex items-center justify-center text-[8px] text-black font-bold">&#10003;</span>}
                    </div>
                    <p className="text-xs font-medium leading-snug">{item.name}</p>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell title="Swell Skills">
      <div className="p-5">
        {/* Pro upgrade banner for free users */}
        {!isPro && (
          <div className="bg-primary/5 border border-primary/15 rounded-2xl p-3 mb-4 flex items-center gap-3">
            <Sparkles className="w-4 h-4 text-primary flex-shrink-0" />
            <p className="text-[11px] text-muted-foreground flex-1">Upgrade to <span className="text-primary font-semibold">Swell Pro</span> to activate skills</p>
            <ProBadge />
          </div>
        )}

        {/* Pro Gate Modal */}
        <AnimatePresence>
          {showProModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowProModal(false)}
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                onClick={e => e.stopPropagation()}
                className="w-full max-w-sm"
              >
                <ProGate feature="automations" />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        {/* Alerts Banner */}
        {unreadCount > 0 && (
          <button
            data-testid="alerts-banner"
            onClick={() => setShowAlerts(true)}
            className="w-full bg-primary/10 border border-primary/20 rounded-2xl p-3 mb-4 flex items-center gap-3 hover:bg-primary/15 transition-colors"
          >
            <AlertCircle className="w-4 h-4 text-primary flex-shrink-0" />
            <span className="text-xs text-primary font-medium flex-1 text-left">{unreadCount} new alert{unreadCount > 1 ? 's' : ''}</span>
            <span className="text-[10px] text-primary/70">View</span>
          </button>
        )}

        {/* Filters + Add + Alerts */}
        <div className="flex flex-wrap items-center gap-2 mb-5">
          {filters.map(f => (
            <button
              key={f}
              data-testid={`filter-${f}`}
              onClick={() => setFilter(f)}
              className={`text-xs px-3 py-1.5 rounded-full transition-colors ${
                filter === f ? 'bg-white/10 text-white' : 'text-muted-foreground hover:text-white'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-2">
            <button
              data-testid="view-alerts-btn"
              onClick={() => setShowAlerts(true)}
              className="relative p-1.5 hover:bg-white/10 rounded-full transition-colors"
            >
              <Bell className="w-4 h-4 text-muted-foreground" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-primary rounded-full flex items-center justify-center text-[8px] text-black font-bold">{unreadCount}</span>
              )}
            </button>
            <button
              data-testid="add-automation-btn"
              onClick={() => gatedAction(() => setShowGallery(true))}
              className="bg-white/10 hover:bg-white/15 text-white text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap inline-flex items-center gap-1 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
        </div>

        {/* Automation List */}
        {filtered.length > 0 ? (
          <div className="space-y-3">
            {filtered.map((auto) => {
              const Icon = TYPE_ICONS[auto.type] || Wand2;
              const isExpanded = expandedId === auto.automation_id;
              return (
                <div
                  key={auto.automation_id}
                  className="bg-bg-subtle/50 border border-white/5 rounded-2xl overflow-hidden"
                  data-testid={`automation-${auto.automation_id}`}
                >
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : auto.automation_id)}
                    className="w-full flex items-start gap-3 p-4 text-left"
                  >
                    <Icon className={`w-4 h-4 mt-0.5 ${TYPE_COLORS[auto.type] || 'text-primary'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">{auto.name}</p>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${auto.enabled ? 'bg-green-500/15 text-green-400' : 'bg-white/5 text-muted-foreground'}`}>
                          {auto.enabled ? 'on' : 'off'}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{auto.description}</p>
                      {auto.trigger_count > 0 && (
                        <p className="text-[9px] text-muted-foreground/60 mt-1">triggered {auto.trigger_count}x</p>
                      )}
                    </div>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-muted-foreground flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-muted-foreground flex-shrink-0" />}
                  </button>
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0 }}
                        animate={{ height: 'auto' }}
                        exit={{ height: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 border-t border-white/5 pt-3">
                          {/* Config Editor */}
                          <ConfigEditor auto={auto} onSave={(cfg) => updateConfig(auto, cfg)} />

                          {/* Actions */}
                          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
                            <button
                              data-testid={`toggle-${auto.automation_id}`}
                              onClick={() => gatedAction(() => toggleEnabled(auto))}
                              className={`text-xs px-3 py-1.5 rounded-full transition-colors ${auto.enabled ? 'bg-primary/20 text-primary' : 'bg-white/5 text-muted-foreground'}`}
                            >
                              {auto.enabled ? 'Enabled' : 'Disabled'}
                            </button>
                            <button
                              data-testid={`trigger-${auto.automation_id}`}
                              onClick={() => gatedAction(() => triggerAutomation(auto.automation_id))}
                              disabled={triggering === auto.automation_id || !auto.enabled}
                              className="text-xs px-3 py-1.5 rounded-full bg-white/5 text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors inline-flex items-center gap-1 disabled:opacity-40"
                            >
                              <Play className="w-3 h-3" />
                              {triggering === auto.automation_id ? 'Running...' : 'Test'}
                            </button>
                            <button
                              data-testid={`delete-${auto.automation_id}`}
                              onClick={() => gatedAction(() => deleteAutomation(auto.automation_id))}
                              className="p-1.5 hover:bg-white/10 rounded-full transition-colors ml-auto"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-muted-foreground" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <Wand2 className="w-8 h-8 text-muted-foreground/50 mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">no skills yet</p>
            <button
              onClick={() => gatedAction(() => setShowGallery(true))}
              className="text-xs text-primary mt-2 hover:underline"
            >
              browse the gallery
            </button>
          </div>
        )}
      </div>
    </PageShell>
  );
}
